/* eslint-disable react-refresh/only-export-components */

import { Table, TableView } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import {
  Fragment,
  type Node as ProseMirrorNode,
  type ResolvedPos,
} from '@tiptap/pm/model';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { CellSelection } from 'prosemirror-tables';
import { useCallback, useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { Icons } from '@/assets/icons/icons';
import { getChangedRanges } from '@/components/ui/editor/utils/transactions';
import {
  SearchableActionDropdown,
  type SearchableAction,
} from '@/components/ui/SearchableActionDropdown';
import { Tooltip, TooltipProvider } from '@/components/ui/Tooltip';
import { afterFrames } from '@/lib/animate';

import {
  buildClipboardPayload,
  escapeMarkdownCell,
  tableClipboardTextPlugin,
} from './table-clipboard';

import type { Editor, NodeViewRendererProps } from '@tiptap/core';

const activeCellPluginKey = new PluginKey('activeCell');

const activeCellPlugin = (editor: Editor) =>
  new Plugin({
    key: activeCellPluginKey,
    state: {
      init() {
        return DecorationSet.empty;
      },
      apply(_tr, _oldState, _, newState) {
        const { selection } = newState;
        const { $from } = selection;

        for (let depth = $from.depth; depth > 0; depth--) {
          const node = $from.node(depth);
          if (
            node.type.name === 'tableCell' ||
            node.type.name === 'tableHeader'
          ) {
            const pos = $from.before(depth);
            const decoration = Decoration.node(pos, pos + node.nodeSize, {
              class: 'lb-editor-table-cell-active',
            });
            return DecorationSet.create(newState.doc, [decoration]);
          }
        }
        return DecorationSet.empty;
      },
    },
    props: {
      decorations(state) {
        return activeCellPluginKey.getState(state);
      },
    },
    view(editorView: EditorView) {
      let cellHandleContainer: HTMLDivElement | null = null;
      let cellHandleRoot: Root | null = null;
      let rowHandleContainer: HTMLDivElement | null = null;
      let rowHandleRoot: Root | null = null;
      let columnHandleContainer: HTMLDivElement | null = null;
      let columnHandleRoot: Root | null = null;
      let pendingAnimationFrame: number | null = null;
      let lastActiveCellElement: HTMLElement | null = null;
      let lastHandleKey: string | null = null;

      const createHandleContainer = (className: string) => {
        const container = document.createElement('div');
        container.className = className;
        const stopEvent = (e: Event) => e.stopPropagation();
        container.addEventListener('mousemove', stopEvent, true);
        container.addEventListener('mousedown', stopEvent, true);
        container.addEventListener('mouseup', stopEvent, true);
        return container;
      };

      const hideAllHandles = () => {
        if (cellHandleContainer) cellHandleContainer.style.display = 'none';
        if (rowHandleContainer) rowHandleContainer.style.display = 'none';
        if (columnHandleContainer) columnHandleContainer.style.display = 'none';
      };

      const updateHandlePositions = () => {
        const activeCell = editorView.dom.querySelector<HTMLElement>(
          '.lb-editor-table-cell-active'
        );
        if (!activeCell) {
          hideAllHandles();
          lastActiveCellElement = null;
          lastHandleKey = null;
          return;
        }

        const activeCellChanged = lastActiveCellElement !== activeCell;
        if (lastActiveCellElement && activeCellChanged) {
          forceHideDropdowns();
          lastActiveCellElement = activeCell;
          lastHandleKey = null;
          return;
        }
        lastActiveCellElement = activeCell;

        const wrapper = activeCell.closest<HTMLElement>('.tableWrapper');
        const table = wrapper?.querySelector('table');
        if (!wrapper || !table) return;
        const viewport = wrapper.querySelector<HTMLElement>(
          ':scope > [data-editor-table-viewport]'
        );
        const canvas = viewport?.querySelector<HTMLElement>(
          ':scope > [data-editor-table-canvas]'
        );
        const headerKey = isHeaderRowStyled(editor) ? 'styled' : 'unstyled';
        const inFirstRowKey = isInFirstRow(editor) ? 'first' : 'other';
        const inFirstColumnKey = isInFirstColumn(editor) ? 'first' : 'other';
        const handleKey = `${headerKey}-${inFirstRowKey}`;
        const handleStateKey = `${handleKey}-${inFirstColumnKey}`;
        if (canvas && !activeCellChanged && lastHandleKey === handleStateKey) {
          return;
        }
        lastHandleKey = handleStateKey;

        const cellRect = activeCell.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const tableRect = table.getBoundingClientRect();
        const viewportRect = viewport?.getBoundingClientRect();
        const canvasRect = canvas?.getBoundingClientRect();
        const cellCenterX = cellRect.left + cellRect.width / 2;
        const cellCenterY = cellRect.top + cellRect.height / 2;

        if (!cellHandleContainer) {
          cellHandleContainer = createHandleContainer(
            'lb-editor-table-cell-handle-container'
          );
          cellHandleRoot = createRoot(cellHandleContainer);
        }
        cellHandleRoot?.render(
          <TableCellHandle key={handleKey} editor={editor} />
        );
        const cellHandleParent = canvas ?? wrapper;
        if (cellHandleContainer.parentElement !== cellHandleParent) {
          cellHandleParent.appendChild(cellHandleContainer);
        }
        const handleX =
          canvas && activeCell.parentElement?.lastElementChild === activeCell
            ? cellRect.left
            : cellRect.right;
        const cellOrigin = canvasRect ?? wrapperRect;
        cellHandleContainer.style.top = `${cellCenterY - cellOrigin.top}px`;
        cellHandleContainer.style.left = `${handleX - cellOrigin.left}px`;
        cellHandleContainer.style.display =
          !viewportRect ||
          (handleX >= viewportRect.left &&
            handleX <= viewportRect.right &&
            cellCenterY >= viewportRect.top &&
            cellCenterY <= viewportRect.bottom)
            ? ''
            : 'none';

        if (!rowHandleContainer) {
          rowHandleContainer = createHandleContainer(
            'lb-editor-table-row-handle-container'
          );
          rowHandleRoot = createRoot(rowHandleContainer);
        }
        rowHandleRoot?.render(
          <TableRowHandle key={handleKey} editor={editor} />
        );
        if (rowHandleContainer.parentElement !== wrapper) {
          wrapper.appendChild(rowHandleContainer);
        }
        rowHandleContainer.style.top = `${cellCenterY - wrapperRect.top}px`;
        rowHandleContainer.style.left = viewportRect
          ? '0px'
          : `${tableRect.left - wrapperRect.left}px`;
        const headerBottom =
          table.querySelector('th')?.getBoundingClientRect().bottom ??
          viewportRect?.top ??
          tableRect.top;
        const minimumY =
          activeCell.tagName === 'TH'
            ? (viewportRect?.top ?? tableRect.top)
            : headerBottom;
        rowHandleContainer.style.display =
          !viewportRect ||
          (cellCenterY >= minimumY && cellCenterY <= viewportRect.bottom)
            ? ''
            : 'none';

        if (!columnHandleContainer) {
          columnHandleContainer = createHandleContainer(
            'lb-editor-table-column-handle-container'
          );
          columnHandleRoot = createRoot(columnHandleContainer);
        }
        columnHandleRoot?.render(
          <TableColumnHandle key={headerKey} editor={editor} />
        );
        if (columnHandleContainer.parentElement !== wrapper) {
          wrapper.appendChild(columnHandleContainer);
        }
        columnHandleContainer.style.top = `${(viewportRect?.top ?? tableRect.top) - wrapperRect.top}px`;
        columnHandleContainer.style.left = `${cellCenterX - wrapperRect.left}px`;
        columnHandleContainer.style.display =
          !viewportRect ||
          (cellCenterX >= viewportRect.left &&
            cellCenterX <= viewportRect.right)
            ? ''
            : 'none';
      };

      return {
        update(view: EditorView) {
          const { $from } = view.state.selection;
          let inCell = false;

          for (let depth = $from.depth; depth > 0; depth--) {
            const node = $from.node(depth);
            if (
              node.type.name === 'tableCell' ||
              node.type.name === 'tableHeader'
            ) {
              inCell = true;
              break;
            }
          }

          if (!inCell) {
            hideAllHandles();
            return;
          }

          // Update positions - first sync for immediate feedback, then async for post-layout
          updateHandlePositions();
          if (pendingAnimationFrame !== null) {
            cancelAnimationFrame(pendingAnimationFrame);
          }
          pendingAnimationFrame = requestAnimationFrame(updateHandlePositions);
        },
        destroy() {
          if (pendingAnimationFrame !== null) {
            cancelAnimationFrame(pendingAnimationFrame);
          }
          cellHandleRoot?.unmount();
          cellHandleContainer?.remove();
          rowHandleRoot?.unmount();
          rowHandleContainer?.remove();
          columnHandleRoot?.unmount();
          columnHandleContainer?.remove();
        },
      };
    },
  });

const tableControlsPluginKey = new PluginKey('tableControls');

const DRAG_THRESHOLD = 50;
const DRAG_THROTTLE_MS = 100;

function TableAddButton({
  type,
  onAdd,
  onRemove,
  dragCleanups,
}: {
  type: 'column' | 'row';
  onAdd: () => void;
  onRemove: () => void;
  dragCleanups: Set<() => void>;
}) {
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const container = (e.currentTarget as HTMLElement).closest(
      '.lb-editor-table-add-column, .lb-editor-table-add-row'
    );
    container?.classList.add('is-dragging');

    const startX = e.clientX;
    const startY = e.clientY;
    let lastX = startX;
    let lastY = startY;
    let hasDragged = false;
    let lastAddTime = 0;
    let lastRemoveTime = 0;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const totalDelta =
        type === 'column'
          ? moveEvent.clientX - startX
          : moveEvent.clientY - startY;

      if (Math.abs(totalDelta) > 5) {
        hasDragged = true;
      }

      const delta =
        type === 'column'
          ? moveEvent.clientX - lastX
          : moveEvent.clientY - lastY;

      const now = Date.now();

      if (delta >= DRAG_THRESHOLD) {
        if (now - lastAddTime >= DRAG_THROTTLE_MS) {
          onAdd();
          lastAddTime = now;
        }
        lastX = moveEvent.clientX;
        lastY = moveEvent.clientY;
      } else if (delta <= -DRAG_THRESHOLD) {
        if (now - lastRemoveTime >= DRAG_THROTTLE_MS) {
          onRemove();
          lastRemoveTime = now;
        }
        lastX = moveEvent.clientX;
        lastY = moveEvent.clientY;
      }
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('blur', handleBlur);
      container?.classList.remove('is-dragging');
      dragCleanups.delete(cleanup);
    };

    const handleMouseUp = () => {
      cleanup();
      if (!hasDragged) {
        onAdd();
      }
    };

    const handleBlur = () => {
      cleanup();
    };

    dragCleanups.add(cleanup);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('blur', handleBlur);
  };

  const cursorClass =
    type === 'column' ? 'cursor-col-resize' : 'cursor-row-resize';

  return (
    <Tooltip
      label={`Click to add a new ${type === 'column' ? 'column' : 'row'}`}
      description={`Drag to add or remove ${type === 'column' ? 'columns' : 'rows'}`}
      side="bottom"
    >
      <button
        type="button"
        className={`w-full h-full flex items-center justify-center ${cursorClass}`}
        onMouseDown={handleMouseDown}
      >
        <svg width="10" height="10" viewBox="0 0 12 12">
          <path
            d="M6 2v8M2 6h8"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </Tooltip>
  );
}

function createAddButton(
  type: 'column' | 'row',
  onAdd: () => void,
  onRemove: () => void,
  dragCleanups: Set<() => void>
): { element: HTMLElement; root: Root } {
  const container = document.createElement('div');
  container.className = `lb-editor-table-add-${type}`;

  const root = createRoot(container);
  root.render(
    <TooltipProvider>
      <TableAddButton
        type={type}
        onAdd={onAdd}
        onRemove={onRemove}
        dragCleanups={dragCleanups}
      />
    </TooltipProvider>
  );

  return { element: container, root };
}

function createRowActions(editor: Editor): SearchableAction[] {
  return [
    {
      id: 'insert-row-above',
      label: 'Insert row above',
      icon: Icons.arrowUp,
      onSelect: () => editor.chain().focus().addRowBefore().run(),
    },
    {
      id: 'insert-row-below',
      label: 'Insert row below',
      icon: Icons.arrowDown,
      onSelect: () => editor.chain().focus().addRowAfter().run(),
    },
    {
      id: 'duplicate-row',
      label: 'Duplicate row',
      icon: Icons.columns,
      onSelect: () => duplicateRow(editor),
    },
  ];
}

function createColumnActions(editor: Editor): SearchableAction[] {
  return [
    {
      id: 'insert-column-left',
      label: 'Insert column left',
      icon: Icons.arrowLeft,
      onSelect: () => editor.chain().focus().addColumnBefore().run(),
    },
    {
      id: 'insert-column-right',
      label: 'Insert column right',
      icon: Icons.navigateRight,
      onSelect: () => editor.chain().focus().addColumnAfter().run(),
    },
    {
      id: 'duplicate-column',
      label: 'Duplicate column',
      icon: Icons.columns,
      onSelect: () => duplicateColumn(editor),
    },
  ];
}

function createHeaderToggleAction(
  editor: Editor,
  headerStyled: boolean
): SearchableAction {
  return {
    id: 'toggle-header',
    label: headerStyled ? 'Remove header style' : 'Style as header',
    iconElement: <ColorSwatch color={headerStyled ? null : HEADER_BG_COLOR} />,
    onSelect: () => toggleHeaderRowBackground(editor),
  };
}

function createDeleteRowAction(editor: Editor): SearchableAction {
  return {
    id: 'delete-row',
    label: 'Delete row',
    icon: Icons.rows,
    variant: 'destructive' as const,
    onSelect: () => {
      afterFrames(25, () => {
        const dims = getTableDimensions(editor);
        if (dims && dims.rows <= 1) {
          editor.chain().focus().deleteTable().run();
        } else {
          editor.chain().focus().deleteRow().run();
        }
      });
    },
  };
}

function createDeleteColumnAction(editor: Editor): SearchableAction {
  return {
    id: 'delete-column',
    label: 'Delete column',
    icon: Icons.columns,
    variant: 'destructive' as const,
    onSelect: () => {
      afterFrames(25, () => {
        const dims = getTableDimensions(editor);
        if (dims && dims.cols <= 1) {
          editor.chain().focus().deleteTable().run();
        } else {
          editor.chain().focus().deleteColumn().run();
        }
      });
    },
  };
}

function createDeleteTableAction(editor: Editor): SearchableAction {
  return {
    id: 'delete-table',
    label: 'Delete table',
    icon: Icons.table,
    variant: 'destructive' as const,
    onSelect: () => {
      // Delay to let dropdown close before removing its anchor
      afterFrames(25, () => {
        editor.chain().focus().deleteTable().run();
      });
    },
  };
}

function createColorActions(editor: Editor): SearchableAction[] {
  return CELL_COLORS.map((color) => ({
    id: `bg-${color.name.toLowerCase()}`,
    label: color.value ? `${color.name} background` : 'Clear background',
    iconElement: <ColorSwatch color={color.value} />,
    onSelect: () => setCellBackgroundColor(editor, color.value),
  }));
}

function createCopyTableAction(editor: Editor): SearchableAction {
  return {
    id: 'copy-table',
    label: 'Copy table',
    icon: Icons.copy,
    onSelect: async () => {
      if (await copyTable(editor)) {
        const { toast } = await import('sonner');
        toast.success('Table copied');
      }
    },
  };
}

function createCopyCellAction(editor: Editor): SearchableAction {
  return {
    id: 'copy-cell',
    label: 'Copy',
    icon: Icons.copy,
    onSelect: async () => {
      if (await copyCell(editor)) {
        const { toast } = await import('sonner');
        toast.success('Copied');
      }
    },
  };
}

function TableCellHandle({ editor }: { editor: Editor }) {
  const inFirstRow = isInFirstRow(editor);
  const headerStyled = inFirstRow ? isHeaderRowStyled(editor) : false;

  const actions: SearchableAction[] = [
    ...createRowActions(editor),
    ...createColumnActions(editor),
    {
      id: 'clear-contents',
      label: 'Clear contents',
      icon: Icons.eraser,
      onSelect: () => clearCellContents(editor),
    },
    createCopyCellAction(editor),
    createCopyTableAction(editor),
    ...createColorActions(editor),
    ...(inFirstRow ? [createHeaderToggleAction(editor, headerStyled)] : []),
    createDeleteRowAction(editor),
    createDeleteColumnAction(editor),
    createDeleteTableAction(editor),
  ];

  return (
    <SearchableActionDropdown
      actions={actions}
      side="left"
      align="start"
      sideOffset={8}
      searchPlaceholder="Search actions..."
    >
      <button
        type="button"
        className="lb-editor-table-cell-handle"
        aria-label="Cell actions"
      >
        <Icons.more className="size-3 text-white" />
      </button>
    </SearchableActionDropdown>
  );
}

function selectEntireRow(editor: Editor): boolean {
  const rowInfo = findCurrentRowInfo(editor);
  const tableInfo = findTableInfo(editor.state.selection.$from);
  if (!rowInfo || !tableInfo) return false;

  const { rowNode, rowPos } = rowInfo;
  if (rowNode.childCount === 0) return false;

  // Get first and last cell positions in the row
  const firstCellPos = rowPos + 1;
  const lastCellPos =
    rowPos +
    rowNode.nodeSize -
    rowNode.child(rowNode.childCount - 1).nodeSize -
    1;

  try {
    const selection = CellSelection.create(
      editor.state.doc,
      firstCellPos,
      lastCellPos
    );
    editor.view.dispatch(editor.state.tr.setSelection(selection));
    return true;
  } catch {
    return false;
  }
}

function createDragGhost(className: string, width?: number): HTMLTableElement {
  const ghost = document.createElement('table');
  ghost.className = className;
  ghost.style.borderSpacing = '0';
  ghost.style.tableLayout = 'fixed';
  if (width) {
    ghost.style.width = `${width}px`;
  }
  return ghost;
}

const TABLE_DRAGGING_BODY_CLASS = 'lb-editor-table-dragging';

function showDragGhost(ghost: HTMLElement): void {
  document.body.appendChild(ghost);
  document.body.classList.add(TABLE_DRAGGING_BODY_CLASS);
}

function removeDragGhost(ghost: HTMLElement | null): void {
  ghost?.remove();
  document.body.classList.remove(TABLE_DRAGGING_BODY_CLASS);
}

function positionGhostAtCursor(
  ghost: HTMLElement,
  clientX: number,
  clientY: number
): void {
  ghost.style.left = `${clientX + 10}px`;
  ghost.style.top = `${clientY - 15}px`;
}

function dismissOpenDropdown(): void {
  if (
    !document.querySelector(
      ':is(.lb-editor-table-cell-handle-container, .lb-editor-table-row-handle-container, .lb-editor-table-column-handle-container, .lb-editor-multi-cell-handle-container) [aria-haspopup="menu"][data-state="open"]'
    )
  ) {
    return;
  }
  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', bubbles: true })
  );
}

// Force-hide any open Radix dropdown content immediately (no animation)
function forceHideDropdowns(): void {
  const dropdownContents = document.querySelectorAll<HTMLElement>(
    '[data-radix-popper-content-wrapper]'
  );
  dropdownContents.forEach((el) => {
    el.style.display = 'none';
    // Reset display after Radix has closed the dropdown
    setTimeout(() => {
      el.style.display = '';
    }, 100);
  });
  dismissOpenDropdown();
}

const DRAG_START_THRESHOLD = 3;

function useRowDrag(editor: Editor) {
  // Store cleanup function in ref for access during unmount
  const cleanupRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount to avoid leaked listeners if editor destroyed mid-drag
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      selectEntireRow(editor);

      const target = e.target as HTMLElement;
      const startY = e.clientY;
      const startX = e.clientX;
      let hasDragged = false;
      let ghost: HTMLElement | null = null;
      let dropIndicator: HTMLElement | null = null;
      let targetRowIndex: number | null = null;

      const rowInfo = findCurrentRowInfo(editor);
      const tableInfo = findTableInfo(editor.state.selection.$from);
      if (!rowInfo || !tableInfo) return;

      const currentRowIndex = getRowIndex(editor);
      if (currentRowIndex === null) return;

      const wrapper = target.closest('.tableWrapper');
      const table = wrapper?.querySelector('table');
      if (!wrapper || !table) return;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const deltaY = Math.abs(moveEvent.clientY - startY);
        const deltaX = Math.abs(moveEvent.clientX - startX);

        if (
          !hasDragged &&
          (deltaY > DRAG_START_THRESHOLD || deltaX > DRAG_START_THRESHOLD)
        ) {
          hasDragged = true;
          // Capture pointer only when drag starts to avoid blocking dropdown clicks
          target.setPointerCapture(e.pointerId);
          dismissOpenDropdown();

          const rows = table.querySelectorAll('tr');
          const currentRow = rows[currentRowIndex];
          if (currentRow) {
            const rowWidth = currentRow.getBoundingClientRect().width;
            ghost = createDragGhost('lb-editor-row-drag-ghost', rowWidth);

            const cells = currentRow.querySelectorAll('td, th');
            const colgroup = document.createElement('colgroup');
            cells.forEach((cell) => {
              const col = document.createElement('col');
              col.style.width = `${cell.getBoundingClientRect().width}px`;
              colgroup.appendChild(col);
            });
            ghost.appendChild(colgroup);

            const tbody = document.createElement('tbody');
            tbody.appendChild(currentRow.cloneNode(true));
            ghost.appendChild(tbody);
            showDragGhost(ghost);
          }

          dropIndicator = document.createElement('div');
          dropIndicator.className = 'lb-editor-row-drop-indicator';
          wrapper.appendChild(dropIndicator);
        }

        if (hasDragged && ghost) {
          positionGhostAtCursor(ghost, moveEvent.clientX, moveEvent.clientY);

          const rows = table.querySelectorAll('tr');
          const wrapperRect = wrapper.getBoundingClientRect();
          let newTargetIndex: number | null = null;

          rows.forEach((row, index) => {
            const rect = row.getBoundingClientRect();
            const midY = rect.top + rect.height / 2;
            if (moveEvent.clientY < midY && newTargetIndex === null) {
              newTargetIndex = index;
            }
          });

          if (newTargetIndex === null) {
            newTargetIndex = rows.length;
          }

          targetRowIndex = newTargetIndex;

          if (dropIndicator) {
            const targetRow = rows[Math.min(targetRowIndex, rows.length - 1)];
            if (targetRow) {
              const rect = targetRow.getBoundingClientRect();
              const indicatorY =
                targetRowIndex >= rows.length
                  ? rect.bottom - wrapperRect.top
                  : rect.top - wrapperRect.top;
              dropIndicator.style.top = `${indicatorY}px`;
              dropIndicator.style.left = `${rect.left - wrapperRect.left}px`;
              dropIndicator.style.width = `${rect.width}px`;
            }
          }
        }
      };

      const cleanup = (performMove = false) => {
        cleanupRef.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
        window.removeEventListener('blur', handleWindowBlur);

        try {
          target.releasePointerCapture(e.pointerId);
        } catch {
          // Pointer may already be released
        }

        if (hasDragged) {
          if (
            performMove &&
            targetRowIndex !== null &&
            targetRowIndex !== currentRowIndex &&
            targetRowIndex !== currentRowIndex + 1
          ) {
            moveRow(editor, currentRowIndex, targetRowIndex);
          }

          removeDragGhost(ghost);
          dropIndicator?.remove();
        }
      };

      // Store cleanup for unmount handling
      cleanupRef.current = () => cleanup(false);

      const handlePointerUp = () => {
        cleanup(true);
      };

      const handlePointerCancel = () => {
        cleanup(false);
      };

      const handleWindowBlur = () => {
        cleanup(false);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      window.addEventListener('blur', handleWindowBlur);
    },
    [editor]
  );

  return { handlePointerDown };
}

function getRowIndex(editor: Editor): number | null {
  const { $from } = editor.state.selection;
  const tableInfo = findTableInfo($from);
  if (!tableInfo) return null;

  const rowInfo = findCurrentRowInfo(editor);
  if (!rowInfo) return null;

  // Calculate row index by counting rows before current
  let rowIndex = 0;
  let pos = tableInfo.pos + 1; // After table open
  for (let i = 0; i < tableInfo.node.childCount; i++) {
    if (pos === rowInfo.rowPos) {
      rowIndex = i;
      break;
    }
    pos += tableInfo.node.child(i).nodeSize;
  }

  return rowIndex;
}

function moveRow(editor: Editor, fromIndex: number, toIndex: number): boolean {
  const { $from } = editor.state.selection;
  const tableInfo = findTableInfo($from);
  if (!tableInfo) return false;

  const { node: tableNode, pos: tablePos } = tableInfo;
  // Don't allow moves in tables with merged cells - would produce invalid structure
  if (hasSpannedCells(tableNode)) return false;
  if (fromIndex < 0 || fromIndex >= tableNode.childCount) return false;
  if (toIndex < 0 || toIndex > tableNode.childCount) return false;
  if (toIndex === fromIndex || toIndex === fromIndex + 1) return false;

  const rowToMove = tableNode.child(fromIndex);

  // Calculate from position (start of row to move)
  let fromPos = tablePos + 1;
  for (let i = 0; i < fromIndex; i++) {
    fromPos += tableNode.child(i).nodeSize;
  }

  // Calculate to position (where to insert)
  let toPos = tablePos + 1;
  for (let i = 0; i < toIndex; i++) {
    toPos += tableNode.child(i).nodeSize;
  }

  const { tr } = editor.state;

  // Delete row from original position
  tr.delete(fromPos, fromPos + rowToMove.nodeSize);

  // Map the insertion position through the deletion
  const mappedToPos = tr.mapping.map(toPos);

  // Insert at mapped position
  tr.insert(mappedToPos, rowToMove);

  // Select the moved row at its new position
  const newRowIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
  const newDoc = tr.doc;
  const newTable = newDoc.nodeAt(tablePos);
  if (newTable) {
    let newRowPos = tablePos + 1;
    for (let i = 0; i < newRowIndex; i++) {
      newRowPos += newTable.child(i).nodeSize;
    }
    const newRow = newTable.child(newRowIndex);
    if (newRow.childCount > 0) {
      const firstCellPos = newRowPos + 1;
      const lastCellPos =
        newRowPos +
        newRow.nodeSize -
        newRow.child(newRow.childCount - 1).nodeSize -
        1;
      try {
        const selection = CellSelection.create(
          newDoc,
          firstCellPos,
          lastCellPos
        );
        tr.setSelection(selection);
      } catch {
        // Selection creation failed, continue without selection
      }
    }
  }

  editor.view.dispatch(tr);
  return true;
}

function TableRowHandle({ editor }: { editor: Editor }) {
  const inFirstRow = isInFirstRow(editor);
  const inFirstColumn = isInFirstColumn(editor);
  const headerStyled = inFirstRow ? isHeaderRowStyled(editor) : false;
  const { handlePointerDown } = useRowDrag(editor);

  // When in first column, show full cell actions (same as cell handle)
  const actions: SearchableAction[] = inFirstColumn
    ? [
        ...createRowActions(editor),
        ...createColumnActions(editor),
        {
          id: 'clear-contents',
          label: 'Clear contents',
          icon: Icons.eraser,
          onSelect: () => clearCellContents(editor),
        },
        createCopyCellAction(editor),
        createCopyTableAction(editor),
        ...createColorActions(editor),
        ...(inFirstRow ? [createHeaderToggleAction(editor, headerStyled)] : []),
        createDeleteRowAction(editor),
        createDeleteColumnAction(editor),
        createDeleteTableAction(editor),
      ]
    : [
        ...(inFirstRow ? [createHeaderToggleAction(editor, headerStyled)] : []),
        ...createRowActions(editor),
        createCopyTableAction(editor),
        createDeleteRowAction(editor),
        createDeleteTableAction(editor),
      ];

  return (
    <SearchableActionDropdown
      actions={actions}
      side="left"
      align="center"
      sideOffset={8}
      searchPlaceholder={
        inFirstColumn ? 'Search actions...' : 'Search row actions...'
      }
    >
      <button
        type="button"
        className="lb-editor-table-row-handle cursor-grab"
        aria-label={inFirstColumn ? 'Cell actions' : 'Row actions'}
        onPointerDown={handlePointerDown}
      >
        <Icons.gripVertical className="size-3 text-white" />
      </button>
    </SearchableActionDropdown>
  );
}

function selectEntireColumn(editor: Editor): boolean {
  const tableInfo = findTableInfo(editor.state.selection.$from);
  const colIndex = getColumnIndex(editor);
  if (!tableInfo || colIndex === null) return false;

  const { node: tableNode, pos: tablePos } = tableInfo;
  if (tableNode.childCount === 0) return false;

  // Get first cell in column (first row)
  const firstRow = tableNode.child(0);
  let firstCellPos = tablePos + 2; // +1 for table, +1 for row
  for (let i = 0; i < colIndex && i < firstRow.childCount; i++) {
    firstCellPos += firstRow.child(i).nodeSize;
  }

  // Get last cell in column (last row)
  const lastRow = tableNode.child(tableNode.childCount - 1);
  let lastCellPos = tablePos + 1; // +1 for table
  for (let i = 0; i < tableNode.childCount - 1; i++) {
    lastCellPos += tableNode.child(i).nodeSize;
  }
  lastCellPos += 1; // +1 for last row
  for (let i = 0; i < colIndex && i < lastRow.childCount; i++) {
    lastCellPos += lastRow.child(i).nodeSize;
  }

  try {
    const selection = CellSelection.create(
      editor.state.doc,
      firstCellPos,
      lastCellPos
    );
    editor.view.dispatch(editor.state.tr.setSelection(selection));
    return true;
  } catch {
    return false;
  }
}

function useColumnDrag(editor: Editor) {
  // Store cleanup function in ref for access during unmount
  const cleanupRef = useRef<(() => void) | null>(null);

  // Cleanup on unmount to avoid leaked listeners if editor destroyed mid-drag
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      selectEntireColumn(editor);

      const target = e.target as HTMLElement;
      const startY = e.clientY;
      const startX = e.clientX;
      let hasDragged = false;
      let ghost: HTMLElement | null = null;
      let dropIndicator: HTMLElement | null = null;
      let targetColIndex: number | null = null;

      const cellInfo = findCurrentCellInfo(editor);
      const tableInfo = findTableInfo(editor.state.selection.$from);
      if (!cellInfo || !tableInfo) return;

      const currentColIndex = getColumnIndex(editor);
      if (currentColIndex === null) return;

      const wrapper = target.closest('.tableWrapper');
      const table = wrapper?.querySelector('table');
      if (!wrapper || !table) return;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const deltaY = Math.abs(moveEvent.clientY - startY);
        const deltaX = Math.abs(moveEvent.clientX - startX);

        if (
          !hasDragged &&
          (deltaY > DRAG_START_THRESHOLD || deltaX > DRAG_START_THRESHOLD)
        ) {
          hasDragged = true;
          // Capture pointer only when drag starts to avoid blocking dropdown clicks
          target.setPointerCapture(e.pointerId);
          dismissOpenDropdown();

          ghost = createDragGhost('lb-editor-column-drag-ghost');

          const rows = table.querySelectorAll('tr');
          const firstCell =
            rows[0]?.querySelectorAll('td, th')[currentColIndex];
          const colWidth = firstCell?.getBoundingClientRect().width ?? 100;

          const colgroup = document.createElement('colgroup');
          const col = document.createElement('col');
          col.style.width = `${colWidth}px`;
          colgroup.appendChild(col);
          ghost.appendChild(colgroup);

          const tbody = document.createElement('tbody');
          rows.forEach((row) => {
            const cells = row.querySelectorAll('td, th');
            const cell = cells[currentColIndex];
            if (cell) {
              const tr = document.createElement('tr');
              tr.appendChild(cell.cloneNode(true));
              tbody.appendChild(tr);
            }
          });
          ghost.appendChild(tbody);

          showDragGhost(ghost);

          dropIndicator = document.createElement('div');
          dropIndicator.className = 'lb-editor-column-drop-indicator';
          wrapper.appendChild(dropIndicator);
        }

        if (hasDragged && ghost) {
          positionGhostAtCursor(ghost, moveEvent.clientX, moveEvent.clientY);

          const rows = table.querySelectorAll('tr');
          const firstRow = rows[0];
          if (!firstRow) return;

          const cells = firstRow.querySelectorAll('td, th');
          const wrapperRect = wrapper.getBoundingClientRect();
          let newTargetIndex: number | null = null;

          cells.forEach((cell, index) => {
            const rect = cell.getBoundingClientRect();
            const midX = rect.left + rect.width / 2;
            if (moveEvent.clientX < midX && newTargetIndex === null) {
              newTargetIndex = index;
            }
          });

          if (newTargetIndex === null) {
            newTargetIndex = cells.length;
          }

          targetColIndex = newTargetIndex;

          if (dropIndicator) {
            const targetCell =
              cells[Math.min(targetColIndex, cells.length - 1)];
            if (targetCell) {
              const rect = targetCell.getBoundingClientRect();
              const tableRect = table.getBoundingClientRect();
              const indicatorX =
                targetColIndex >= cells.length
                  ? rect.right - wrapperRect.left
                  : rect.left - wrapperRect.left;
              dropIndicator.style.left = `${indicatorX}px`;
              dropIndicator.style.top = `${tableRect.top - wrapperRect.top}px`;
              dropIndicator.style.height = `${tableRect.height}px`;
            }
          }
        }
      };

      const cleanup = (performMove = false) => {
        cleanupRef.current = null;
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerCancel);
        window.removeEventListener('blur', handleWindowBlur);

        try {
          target.releasePointerCapture(e.pointerId);
        } catch {
          // Pointer may already be released
        }

        if (hasDragged) {
          if (
            performMove &&
            targetColIndex !== null &&
            targetColIndex !== currentColIndex &&
            targetColIndex !== currentColIndex + 1
          ) {
            moveColumn(editor, currentColIndex, targetColIndex);
          }

          removeDragGhost(ghost);
          dropIndicator?.remove();
        }
      };

      // Store cleanup for unmount handling
      cleanupRef.current = () => cleanup(false);

      const handlePointerUp = () => {
        cleanup(true);
      };

      const handlePointerCancel = () => {
        cleanup(false);
      };

      const handleWindowBlur = () => {
        cleanup(false);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
      document.addEventListener('pointercancel', handlePointerCancel);
      window.addEventListener('blur', handleWindowBlur);
    },
    [editor]
  );

  return { handlePointerDown };
}

function getColumnIndex(editor: Editor): number | null {
  const rowInfo = findCurrentRowInfo(editor);
  const cellInfo = findCurrentCellInfo(editor);
  if (!rowInfo || !cellInfo) return null;

  // Calculate column index by counting cells before current in the row
  let colIndex = 0;
  let pos = rowInfo.rowPos + 1; // After row open
  for (let i = 0; i < rowInfo.rowNode.childCount; i++) {
    if (pos === cellInfo.cellPos) {
      colIndex = i;
      break;
    }
    pos += rowInfo.rowNode.child(i).nodeSize;
  }

  return colIndex;
}

function moveColumn(
  editor: Editor,
  fromIndex: number,
  toIndex: number
): boolean {
  const { $from } = editor.state.selection;
  const tableInfo = findTableInfo($from);
  if (!tableInfo) return false;

  const { node: tableNode, pos: tablePos } = tableInfo;
  // Don't allow moves in tables with merged cells - would produce invalid structure
  if (hasSpannedCells(tableNode)) return false;
  const firstRow = tableNode.child(0);
  if (!firstRow) return false;

  const colCount = firstRow.childCount;
  if (fromIndex < 0 || fromIndex >= colCount) return false;
  if (toIndex < 0 || toIndex > colCount) return false;
  if (toIndex === fromIndex || toIndex === fromIndex + 1) return false;

  const { tr } = editor.state;

  // Move column in each row (process in reverse to maintain positions)
  for (let rowIdx = tableNode.childCount - 1; rowIdx >= 0; rowIdx--) {
    const row = tableNode.child(rowIdx);
    const cellToMove = row.child(fromIndex);

    // Calculate row position
    let rowPos = tablePos + 1;
    for (let i = 0; i < rowIdx; i++) {
      rowPos += tableNode.child(i).nodeSize;
    }

    // Calculate from cell position
    let fromCellPos = rowPos + 1;
    for (let i = 0; i < fromIndex; i++) {
      fromCellPos += row.child(i).nodeSize;
    }

    // Calculate to cell position
    let toCellPos = rowPos + 1;
    for (let i = 0; i < toIndex; i++) {
      toCellPos += row.child(i).nodeSize;
    }

    // Delete cell from original position
    tr.delete(fromCellPos, fromCellPos + cellToMove.nodeSize);

    // Map the insertion position through the deletion
    const mappedToCellPos = tr.mapping.map(toCellPos);

    // Insert at mapped position
    tr.insert(mappedToCellPos, cellToMove);
  }

  // Select the moved column at its new position
  const newColIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
  const newDoc = tr.doc;
  const newTable = newDoc.nodeAt(tablePos);
  if (newTable && newTable.childCount > 0) {
    // Get first cell in column (first row)
    const firstRowPos = tablePos + 1;
    const newFirstRow = newTable.child(0);
    let firstCellPos = firstRowPos + 1;
    for (let i = 0; i < newColIndex && i < newFirstRow.childCount; i++) {
      firstCellPos += newFirstRow.child(i).nodeSize;
    }

    // Get last cell in column (last row)
    let lastRowPos = tablePos + 1;
    for (let i = 0; i < newTable.childCount - 1; i++) {
      lastRowPos += newTable.child(i).nodeSize;
    }
    const newLastRow = newTable.child(newTable.childCount - 1);
    let lastCellPos = lastRowPos + 1;
    for (let i = 0; i < newColIndex && i < newLastRow.childCount; i++) {
      lastCellPos += newLastRow.child(i).nodeSize;
    }

    try {
      const selection = CellSelection.create(newDoc, firstCellPos, lastCellPos);
      tr.setSelection(selection);
    } catch {
      // Selection creation failed, continue without selection
    }
  }

  editor.view.dispatch(tr);
  return true;
}

function TableColumnHandle({ editor }: { editor: Editor }) {
  const inFirstRow = isInFirstRow(editor);
  const headerStyled = inFirstRow ? isHeaderRowStyled(editor) : false;
  const { handlePointerDown } = useColumnDrag(editor);

  const actions: SearchableAction[] = [
    ...(inFirstRow ? [createHeaderToggleAction(editor, headerStyled)] : []),
    ...createColumnActions(editor),
    createCopyTableAction(editor),
    createDeleteColumnAction(editor),
    createDeleteTableAction(editor),
  ];

  return (
    <SearchableActionDropdown
      actions={actions}
      side="top"
      align="center"
      sideOffset={8}
      searchPlaceholder="Search column actions..."
    >
      <button
        type="button"
        className="lb-editor-table-column-handle cursor-grab"
        aria-label="Column actions"
        onPointerDown={handlePointerDown}
      >
        <Icons.gripVertical className="size-3 text-white" />
      </button>
    </SearchableActionDropdown>
  );
}

function findTableInfo($from: ResolvedPos): {
  node: ProseMirrorNode;
  pos: number;
  depth: number;
} | null {
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'table') {
      return { node, pos: $from.before(depth), depth };
    }
  }
  return null;
}

function hasSpannedCells(tableNode: ProseMirrorNode): boolean {
  for (let row = 0; row < tableNode.childCount; row++) {
    const rowNode = tableNode.child(row);
    for (let col = 0; col < rowNode.childCount; col++) {
      const cell = rowNode.child(col);
      if ((cell.attrs.colspan ?? 1) > 1 || (cell.attrs.rowspan ?? 1) > 1) {
        return true;
      }
    }
  }
  return false;
}

function isCellEmpty(cell: ProseMirrorNode): boolean {
  if (cell.textContent.trim() !== '') return false;
  // Check for non-text content (images, embeds, mentions, etc.)
  let hasNonTextContent = false;
  cell.descendants((node) => {
    if (node.isLeaf && !node.isText) {
      hasNonTextContent = true;
      return false;
    }
    return true;
  });
  return !hasNonTextContent;
}

function isLastColumnEmpty(editor: Editor): boolean {
  const { state } = editor;
  const tableInfo = findTableInfo(state.selection.$from);
  if (!tableInfo) return false;

  const { node: tableNode } = tableInfo;
  if (hasSpannedCells(tableNode)) return false;

  const colCount = tableNode.child(0)?.childCount ?? 0;
  if (colCount <= 1) return false;

  for (let row = 0; row < tableNode.childCount; row++) {
    const rowNode = tableNode.child(row);
    const lastCell = rowNode.child(rowNode.childCount - 1);
    if (!isCellEmpty(lastCell)) return false;
  }
  return true;
}

function isLastRowEmpty(editor: Editor): boolean {
  const { state } = editor;
  const tableInfo = findTableInfo(state.selection.$from);
  if (!tableInfo) return false;

  const { node: tableNode } = tableInfo;
  if (hasSpannedCells(tableNode)) return false;
  if (tableNode.childCount <= 1) return false;

  const lastRow = tableNode.child(tableNode.childCount - 1);
  for (let col = 0; col < lastRow.childCount; col++) {
    if (!isCellEmpty(lastRow.child(col))) return false;
  }
  return true;
}

function deleteLastColumn(editor: Editor): boolean {
  if (!isLastColumnEmpty(editor)) return false;

  const { state } = editor;
  const tableInfo = findTableInfo(state.selection.$from);
  if (!tableInfo) return false;

  const { node: tableNode, pos: tablePos } = tableInfo;
  const firstRow = tableNode.child(0);
  const colCount = firstRow.childCount;
  if (colCount <= 1) return false;

  // Calculate position of the last cell in first row
  let cellPos = tablePos + 1; // Start after table opening
  cellPos += 1; // After first row opening
  for (let col = 0; col < colCount - 1; col++) {
    cellPos += firstRow.child(col).nodeSize;
  }

  // Select the last column cell and delete
  editor
    .chain()
    .focus()
    .setTextSelection(cellPos + 1)
    .deleteColumn()
    .run();
  return true;
}

function deleteLastRow(editor: Editor): boolean {
  if (!isLastRowEmpty(editor)) return false;

  const { state } = editor;
  const tableInfo = findTableInfo(state.selection.$from);
  if (!tableInfo) return false;

  const { node: tableNode, pos: tablePos } = tableInfo;
  if (tableNode.childCount <= 1) return false;

  // Calculate position of the last row
  let rowPos = tablePos + 1; // Start after table opening
  for (let row = 0; row < tableNode.childCount - 1; row++) {
    rowPos += tableNode.child(row).nodeSize;
  }

  // Select a cell in the last row and delete
  editor
    .chain()
    .focus()
    .setTextSelection(rowPos + 2)
    .deleteRow()
    .run();
  return true;
}

function findCurrentCellInfo(editor: Editor): {
  cellNode: ProseMirrorNode;
  cellPos: number;
  cellDepth: number;
} | null {
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
      return { cellNode: node, cellPos: $from.before(depth), cellDepth: depth };
    }
  }
  return null;
}

function findCurrentRowInfo(editor: Editor): {
  rowNode: ProseMirrorNode;
  rowPos: number;
  rowDepth: number;
} | null {
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'tableRow') {
      return { rowNode: node, rowPos: $from.before(depth), rowDepth: depth };
    }
  }
  return null;
}

function duplicateRow(editor: Editor): boolean {
  const rowInfo = findCurrentRowInfo(editor);
  if (!rowInfo) return false;

  const { rowNode, rowPos } = rowInfo;
  const insertPos = rowPos + rowNode.nodeSize;
  const { tr, schema } = editor.state;

  // Convert tableHeader cells to tableCell for non-first row duplicates
  // since tableHeader should only be in the first row
  const tableCellType = schema.nodes.tableCell;
  const newCells: ProseMirrorNode[] = [];

  for (let i = 0; i < rowNode.childCount; i++) {
    const cell = rowNode.child(i);
    if (cell.type.name === 'tableHeader' && tableCellType) {
      // Convert header cell to regular cell, preserving content and attrs
      newCells.push(
        tableCellType.create(
          { ...cell.attrs, headerStyled: false },
          cell.content,
          cell.marks
        )
      );
    } else {
      newCells.push(cell.copy(cell.content));
    }
  }

  const newRow = rowNode.copy(Fragment.from(newCells));
  tr.insert(insertPos, newRow);
  editor.view.dispatch(tr);
  return true;
}

function duplicateColumn(editor: Editor): boolean {
  const tableInfo = findTableInfo(editor.state.selection.$from);
  const colIndex = getColumnIndex(editor);
  if (!tableInfo || colIndex === null) return false;

  const { node: tableNode, pos: tablePos } = tableInfo;
  const { tr, schema } = editor.state;
  const tableCellType = schema.nodes.tableCell;

  // Process rows from last to first to maintain valid positions
  for (let rowIdx = tableNode.childCount - 1; rowIdx >= 0; rowIdx--) {
    const row = tableNode.child(rowIdx);
    if (colIndex >= row.childCount) continue;

    const cell = row.child(colIndex);

    // Calculate position after the cell to duplicate
    let cellPos = tablePos + 1; // after table start
    for (let r = 0; r < rowIdx; r++) {
      cellPos += tableNode.child(r).nodeSize;
    }
    cellPos += 1; // after row start
    for (let c = 0; c <= colIndex; c++) {
      cellPos += row.child(c).nodeSize;
    }

    // For non-first rows, convert header cells to regular cells
    let newCell: ProseMirrorNode;
    if (rowIdx > 0 && cell.type.name === 'tableHeader' && tableCellType) {
      newCell = tableCellType.create(
        { ...cell.attrs, headerStyled: false },
        cell.content,
        cell.marks
      );
    } else {
      newCell = cell.copy(cell.content);
    }

    tr.insert(cellPos, newCell);
  }

  editor.view.dispatch(tr);
  return true;
}

function clearCellContents(editor: Editor): boolean {
  const cellInfo = findCurrentCellInfo(editor);
  if (!cellInfo) return false;

  const { cellPos, cellNode } = cellInfo;
  const { tr, schema } = editor.state;
  const from = cellPos + 1;
  const to = cellPos + cellNode.nodeSize - 1;

  // Replace with empty paragraph to maintain valid schema (cells require a block)
  const emptyParagraph = schema.nodes.paragraph.create();
  tr.replaceWith(from, to, emptyParagraph);
  editor.view.dispatch(tr);
  return true;
}

async function copyTable(editor: Editor): Promise<boolean> {
  const tableInfo = findTableInfo(editor.state.selection.$from);
  if (!tableInfo) return false;

  const { node: tableNode } = tableInfo;
  const rows: string[][] = [];

  for (let rowIdx = 0; rowIdx < tableNode.childCount; rowIdx++) {
    const row = tableNode.child(rowIdx);
    const cells: string[] = [];
    for (let cellIdx = 0; cellIdx < row.childCount; cellIdx++) {
      cells.push(escapeMarkdownCell(row.child(cellIdx).textContent));
    }
    rows.push(cells);
  }

  // Build markdown table
  const lines: string[] = [];
  if (rows.length > 0) {
    lines.push('| ' + rows[0].join(' | ') + ' |');
    lines.push('| ' + rows[0].map(() => '---').join(' | ') + ' |');
    for (let i = 1; i < rows.length; i++) {
      lines.push('| ' + rows[i].join(' | ') + ' |');
    }
  }

  if (!navigator?.clipboard?.writeText) {
    const { toast } = await import('sonner');
    toast.error('Clipboard not available. Use Cmd/Ctrl+C instead.');
    return false;
  }

  try {
    await navigator.clipboard.writeText(lines.join('\n'));
    return true;
  } catch {
    const { toast } = await import('sonner');
    toast.error(
      "Couldn't copy. Try Cmd/Ctrl+C, or check clipboard permissions."
    );
    return false;
  }
}

async function copyCell(editor: Editor): Promise<boolean> {
  const cellInfo = findCurrentCellInfo(editor);
  if (!cellInfo) return false;

  if (!navigator?.clipboard?.writeText) {
    const { toast } = await import('sonner');
    toast.error('Clipboard not available. Use Cmd/Ctrl+C instead.');
    return false;
  }

  try {
    await navigator.clipboard.writeText(cellInfo.cellNode.textContent);
    return true;
  } catch {
    const { toast } = await import('sonner');
    toast.error(
      "Couldn't copy. Try Cmd/Ctrl+C, or check clipboard permissions."
    );
    return false;
  }
}

function getTableDimensions(editor: Editor): {
  rows: number;
  cols: number;
} | null {
  const tableInfo = findTableInfo(editor.state.selection.$from);
  if (!tableInfo) return null;
  const { node: tableNode } = tableInfo;
  const rows = tableNode.childCount;
  const cols = tableNode.child(0)?.childCount ?? 0;
  return { rows, cols };
}

function selectLastColumnCell(editor: Editor, wrapper: HTMLElement): boolean {
  const table = wrapper.querySelector('table');
  if (!table) return false;

  // Find the table position in the document
  const view = editor.view;
  const tablePos = view.posAtDOM(table, 0);
  if (tablePos < 0) return false;

  const { state } = editor;
  const $pos = state.doc.resolve(tablePos);

  // Find the table node
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'table') {
      const tableStart = $pos.before(depth);
      const firstRow = node.child(0);
      const colCount = firstRow.childCount;

      // Calculate position of last cell in first row
      let cellPos = tableStart + 1; // After table opening
      cellPos += 1; // After first row opening
      for (let col = 0; col < colCount - 1; col++) {
        cellPos += firstRow.child(col).nodeSize;
      }

      editor
        .chain()
        .focus()
        .setTextSelection(cellPos + 1)
        .run();
      return true;
    }
  }
  return false;
}

function selectLastRowCell(editor: Editor, wrapper: HTMLElement): boolean {
  const table = wrapper.querySelector('table');
  if (!table) return false;

  const view = editor.view;
  const tablePos = view.posAtDOM(table, 0);
  if (tablePos < 0) return false;

  const { state } = editor;
  const $pos = state.doc.resolve(tablePos);

  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'table') {
      const tableStart = $pos.before(depth);

      // Calculate position of last row
      let rowPos = tableStart + 1;
      for (let row = 0; row < node.childCount - 1; row++) {
        rowPos += node.child(row).nodeSize;
      }

      editor
        .chain()
        .focus()
        .setTextSelection(rowPos + 2)
        .run();
      return true;
    }
  }
  return false;
}

const tableControlsPlugin = (editor: Editor) =>
  new Plugin({
    key: tableControlsPluginKey,
    view(editorView: EditorView) {
      type WrapperControls = {
        colBtn: HTMLElement;
        rowBtn: HTMLElement;
        colRoot: Root;
        rowRoot: Root;
      };
      const wrapperControls = new Map<HTMLElement, WrapperControls>();
      const dragCleanups = new Set<() => void>();

      const addButtonsToWrapper = (wrapper: HTMLElement) => {
        if (wrapperControls.has(wrapper)) return;

        wrapper.style.position = 'relative';

        const addCol = createAddButton(
          'column',
          () => {
            if (selectLastColumnCell(editor, wrapper)) {
              editor.chain().addColumnAfter().run();
            }
          },
          () => {
            if (selectLastColumnCell(editor, wrapper)) {
              deleteLastColumn(editor);
            }
          },
          dragCleanups
        );
        const addRow = createAddButton(
          'row',
          () => {
            if (selectLastRowCell(editor, wrapper)) {
              editor.chain().addRowAfter().run();
            }
          },
          () => {
            if (selectLastRowCell(editor, wrapper)) {
              deleteLastRow(editor);
            }
          },
          dragCleanups
        );

        wrapper.appendChild(addCol.element);
        wrapper.appendChild(addRow.element);
        wrapperControls.set(wrapper, {
          colBtn: addCol.element,
          rowBtn: addRow.element,
          colRoot: addCol.root,
          rowRoot: addRow.root,
        });
      };

      const cleanupWrapper = (wrapper: HTMLElement) => {
        const controls = wrapperControls.get(wrapper);
        if (!controls) return;
        controls.colRoot.unmount();
        controls.rowRoot.unmount();
        controls.colBtn.remove();
        controls.rowBtn.remove();
        wrapperControls.delete(wrapper);
      };

      const updateButtonPositions = () => {
        const wrappers =
          editorView.dom.querySelectorAll<HTMLElement>('.tableWrapper');
        const currentWrappers = new Set(wrappers);

        // Clean up removed wrappers
        for (const wrapper of wrapperControls.keys()) {
          if (!currentWrappers.has(wrapper)) {
            cleanupWrapper(wrapper);
          }
        }

        wrappers.forEach((wrapper) => {
          addButtonsToWrapper(wrapper);

          const controls = wrapperControls.get(wrapper);
          const table = wrapper.querySelector('table');
          if (!controls || !table) return;

          const wrapperRect = wrapper.getBoundingClientRect();
          const tableRect = table.getBoundingClientRect();

          // Position column button at table's right edge
          const tableRight = tableRect.right - wrapperRect.left;
          controls.colBtn.style.left = `${tableRight}px`;
          controls.colBtn.style.right = 'auto';
          controls.colBtn.style.top = `${tableRect.top - wrapperRect.top}px`;
          controls.colBtn.style.bottom = 'auto';
          controls.colBtn.style.height = `${tableRect.height}px`;

          // Position row button at table's bottom edge
          const tableBottom = tableRect.bottom - wrapperRect.top;
          controls.rowBtn.style.top = `${tableBottom}px`;
          controls.rowBtn.style.bottom = 'auto';
          controls.rowBtn.style.left = `${tableRect.left - wrapperRect.left}px`;
          controls.rowBtn.style.right = 'auto';
          controls.rowBtn.style.width = `${tableRect.width}px`;
        });
      };

      let prevDoc = editorView.state.doc;

      // RAF-throttled update for scroll/resize to avoid jank
      let updateFrame: number | null = null;
      const throttledUpdate = () => {
        if (updateFrame !== null) return;
        updateFrame = requestAnimationFrame(() => {
          updateFrame = null;
          updateButtonPositions();
        });
      };

      updateButtonPositions();
      throttledUpdate();

      // Find the actual scrollable ancestor using computed styles
      const findScrollableParent = (
        element: HTMLElement | null
      ): HTMLElement | Window => {
        if (!element) return window;
        let parent = element.parentElement;
        while (parent) {
          const { overflow, overflowY } = window.getComputedStyle(parent);
          if (
            overflow === 'auto' ||
            overflow === 'scroll' ||
            overflowY === 'auto' ||
            overflowY === 'scroll'
          ) {
            return parent;
          }
          parent = parent.parentElement;
        }
        return window;
      };

      // Update positions on scroll and resize
      const scrollContainer = findScrollableParent(editorView.dom);
      scrollContainer.addEventListener('scroll', throttledUpdate, {
        passive: true,
      });

      const resizeObserver = new ResizeObserver(throttledUpdate);
      resizeObserver.observe(editorView.dom);

      return {
        update(view: EditorView) {
          const { doc } = view.state;
          if (doc === prevDoc) return;
          prevDoc = doc;

          throttledUpdate();
        },
        destroy() {
          scrollContainer.removeEventListener('scroll', throttledUpdate);
          resizeObserver.disconnect();
          if (updateFrame !== null) cancelAnimationFrame(updateFrame);
          for (const wrapper of wrapperControls.keys()) {
            cleanupWrapper(wrapper);
          }
          // Clean up any active drag listeners
          for (const cleanup of dragCleanups) {
            cleanup();
          }
        },
      };
    },
  });

const cellDragSelectionPluginKey = new PluginKey('cellDragSelection');

// Find cell position at document position
function findCellAtPos(
  $pos: ResolvedPos
): { cellPos: number; cellNode: ProseMirrorNode } | null {
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
      return { cellPos: $pos.before(depth), cellNode: node };
    }
  }
  return null;
}

function createMultiCellColorActions(editor: Editor): SearchableAction[] {
  return CELL_COLORS.map((color) => ({
    id: `bg-${color.name.toLowerCase()}`,
    label: color.value ? `${color.name} background` : 'Clear background',
    iconElement: <ColorSwatch color={color.value} />,
    onSelect: () => setSelectedCellsBackgroundColor(editor, color.value),
  }));
}

async function copySelectedCells(editor: Editor): Promise<boolean> {
  const payload = buildClipboardPayload(editor);
  if (!payload) {
    const { toast } = await import('sonner');
    toast.error(
      "This selection can't be copied cleanly. Use Cmd/Ctrl+C to copy it instead."
    );
    return false;
  }

  // Prefer write([ClipboardItem]) so rich destinations also get the
  // table HTML. Fall back to writeText (plain markdown) when the dual-
  // format API isn't available or fails.
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([payload.text], { type: 'text/plain' }),
          'text/html': new Blob([payload.html], { type: 'text/html' }),
        }),
      ]);
      return true;
    } catch {
      // fall through to text-only fallback
    }
  }

  if (!navigator?.clipboard?.writeText) {
    const { toast } = await import('sonner');
    toast.error('Clipboard not available. Use Cmd/Ctrl+C instead.');
    return false;
  }

  try {
    await navigator.clipboard.writeText(payload.text);
    return true;
  } catch {
    const { toast } = await import('sonner');
    toast.error(
      "Couldn't copy. Try Cmd/Ctrl+C, or check clipboard permissions."
    );
    return false;
  }
}

function createCopySelectionAction(editor: Editor): SearchableAction {
  return {
    id: 'copy-selection',
    label: 'Copy',
    icon: Icons.copy,
    onSelect: async () => {
      if (await copySelectedCells(editor)) {
        const { toast } = await import('sonner');
        toast.success('Copied');
      }
    },
  };
}

function MultiCellSelectionHandle({ editor }: { editor: Editor }) {
  const actions: SearchableAction[] = [
    {
      id: 'clear-contents',
      label: 'Clear contents',
      icon: Icons.eraser,
      onSelect: () => clearSelectedCells(editor),
    },
    createCopySelectionAction(editor),
    ...createMultiCellColorActions(editor),
  ];

  return (
    <SearchableActionDropdown
      actions={actions}
      side="right"
      align="center"
      sideOffset={8}
      searchPlaceholder="Search actions..."
    >
      <button
        type="button"
        className="lb-editor-table-cell-handle"
        aria-label="Selection actions"
      >
        <Icons.more className="size-3 text-white" />
      </button>
    </SearchableActionDropdown>
  );
}

function clearSelectedCells(editor: Editor): boolean {
  const { selection, tr, schema } = editor.state;
  if (!(selection instanceof CellSelection)) return false;

  const emptyParagraph = schema.nodes.paragraph.create();

  // Collect cells first, then process in reverse order (highest pos first)
  // to avoid position shifts invalidating subsequent operations
  const cells: Array<{ pos: number; nodeSize: number }> = [];
  selection.forEachCell((cell, pos) => {
    cells.push({ pos, nodeSize: cell.nodeSize });
  });

  cells.sort((a, b) => b.pos - a.pos);

  for (const { pos, nodeSize } of cells) {
    const from = pos + 1;
    const to = pos + nodeSize - 1;
    tr.replaceWith(from, to, emptyParagraph);
  }

  editor.view.dispatch(tr);
  return true;
}

function setSelectedCellsBackgroundColor(
  editor: Editor,
  color: string | null
): boolean {
  const { selection, tr } = editor.state;
  if (!(selection instanceof CellSelection)) return false;

  // Find the table to determine first row position
  const $anchor = selection.$anchorCell;
  const tableInfo = findTableInfo($anchor);
  const firstRowEnd = tableInfo
    ? tableInfo.pos + 1 + tableInfo.node.child(0).nodeSize
    : 0;

  selection.forEachCell((cell, pos) => {
    // Check if cell is in first row by comparing position
    const inFirstRow = tableInfo ? pos < firstRowEnd : false;

    // Align with setCellBackgroundColor: set headerStyled for first-row gray
    const headerStyled =
      inFirstRow && color === HEADER_BG_COLOR
        ? true
        : inFirstRow && color !== HEADER_BG_COLOR
          ? false
          : cell.attrs.headerStyled;

    tr.setNodeMarkup(pos, undefined, {
      ...cell.attrs,
      backgroundColor: color,
      headerStyled,
    });
  });

  editor.view.dispatch(tr);
  return true;
}

const cellDragSelectionPlugin = (editor: Editor) => {
  // Track drag state outside ProseMirror to avoid timing issues
  let anchorCellPos: number | null = null;
  let dragWrapper: HTMLElement | null = null;
  let selectionRing: HTMLElement | null = null;
  let handleContainer: HTMLElement | null = null;
  let handleRoot: Root | null = null;
  let pendingRingUpdate: number | null = null;

  const resetDragState = () => {
    dragWrapper?.classList.remove('is-cell-dragging');
    anchorCellPos = null;
    dragWrapper = null;
  };

  const handleWindowBlur = () => {
    resetDragState();
  };

  const cleanup = () => {
    if (pendingRingUpdate !== null) {
      cancelAnimationFrame(pendingRingUpdate);
      pendingRingUpdate = null;
    }
    if (selectionRing) {
      selectionRing.remove();
      selectionRing = null;
    }
    if (handleRoot) {
      handleRoot.unmount();
      handleRoot = null;
    }
    if (handleContainer) {
      handleContainer.remove();
      handleContainer = null;
    }
  };

  const updateSelectionRing = (view: EditorView) => {
    const { selection } = view.state;

    // Check if we have a multi-cell selection
    // CellSelection has single range spanning cells, so check anchor vs head position
    if (!(selection instanceof CellSelection)) {
      cleanup();
      return;
    }

    const isMultiCell = selection.$anchorCell.pos !== selection.$headCell.pos;
    if (!isMultiCell) {
      cleanup();
      return;
    }

    // Find all selected cell DOM elements
    const selectedCells = view.dom.querySelectorAll('.selectedCell');
    if (selectedCells.length === 0) {
      cleanup();
      return;
    }

    // Calculate bounding box of all selected cells
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    selectedCells.forEach((cell) => {
      const rect = cell.getBoundingClientRect();
      minX = Math.min(minX, rect.left);
      minY = Math.min(minY, rect.top);
      maxX = Math.max(maxX, rect.right);
      maxY = Math.max(maxY, rect.bottom);
    });

    // Find the table wrapper for positioning
    const firstCell = selectedCells[0] as HTMLElement;
    const wrapper = firstCell.closest('.tableWrapper') as HTMLElement;
    if (!wrapper) return;

    const wrapperRect = wrapper.getBoundingClientRect();

    // Create or update the selection ring
    if (!selectionRing) {
      selectionRing = document.createElement('div');
      selectionRing.className = 'lb-editor-cell-selection-ring';
      wrapper.appendChild(selectionRing);
    }

    // Position relative to wrapper
    const ringLeft = minX - wrapperRect.left - 1;
    const ringTop = minY - wrapperRect.top - 1;
    const ringWidth = maxX - minX + 2;
    const ringHeight = maxY - minY + 2;

    selectionRing.style.left = `${ringLeft}px`;
    selectionRing.style.top = `${ringTop}px`;
    selectionRing.style.width = `${ringWidth}px`;
    selectionRing.style.height = `${ringHeight}px`;

    // Create or update the handle
    if (!handleContainer) {
      handleContainer = document.createElement('div');
      handleContainer.className = 'lb-editor-multi-cell-handle-container';
      wrapper.appendChild(handleContainer);
      handleRoot = createRoot(handleContainer);
      handleRoot.render(
        <TooltipProvider>
          <MultiCellSelectionHandle editor={editor} />
        </TooltipProvider>
      );
    }

    // Position handle at right middle edge
    handleContainer.style.left = `${ringLeft + ringWidth}px`;
    handleContainer.style.top = `${ringTop + ringHeight / 2}px`;
  };

  // Document-level mouseup to catch releases outside the editor
  const handleDocumentMouseUp = () => {
    resetDragState();
  };

  return new Plugin({
    key: cellDragSelectionPluginKey,
    view() {
      window.addEventListener('blur', handleWindowBlur);
      document.addEventListener('mouseup', handleDocumentMouseUp);

      return {
        update(view) {
          // Throttle selection ring updates to once per frame
          if (pendingRingUpdate !== null) return;
          pendingRingUpdate = requestAnimationFrame(() => {
            pendingRingUpdate = null;
            updateSelectionRing(view);
          });
        },
        destroy() {
          window.removeEventListener('blur', handleWindowBlur);
          document.removeEventListener('mouseup', handleDocumentMouseUp);
          cleanup();
        },
      };
    },
    props: {
      handleDOMEvents: {
        mousedown(view, event) {
          // Only handle left mouse button
          if (event.button !== 0) {
            anchorCellPos = null;
            return false;
          }

          const pos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });
          if (!pos) {
            anchorCellPos = null;
            return false;
          }

          const $pos = view.state.doc.resolve(pos.pos);
          const cellInfo = findCellAtPos($pos);
          if (!cellInfo) {
            anchorCellPos = null;
            return false;
          }

          // Store anchor cell for drag selection
          anchorCellPos = cellInfo.cellPos;

          // Add class to disable text selection while potentially dragging
          const anchorDom = view.nodeDOM(anchorCellPos);
          dragWrapper = (anchorDom as HTMLElement)?.closest('.tableWrapper');
          dragWrapper?.classList.add('is-cell-dragging');

          return false; // Don't prevent default - allow normal cursor placement
        },
        mousemove(view, event) {
          // Only proceed if left mouse button is pressed and we have an anchor
          if (!(event.buttons & 1) || anchorCellPos === null) return false;

          const pos = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });
          if (!pos) return false;

          const $pos = view.state.doc.resolve(pos.pos);
          const cellInfo = findCellAtPos($pos);
          if (!cellInfo) return false;

          // If we've moved to a different cell, create a cell selection
          if (cellInfo.cellPos !== anchorCellPos) {
            const $anchor = view.state.doc.resolve(anchorCellPos);

            // Verify both cells are in the same table
            const anchorTable = findTableInfo($anchor);
            const headTable = findTableInfo($pos);
            if (!anchorTable || !headTable) return false;
            if (anchorTable.pos !== headTable.pos) return false;

            try {
              const cellSelection = CellSelection.create(
                view.state.doc,
                anchorCellPos,
                cellInfo.cellPos
              );
              view.dispatch(view.state.tr.setSelection(cellSelection));
              return true;
            } catch {
              // CellSelection.create can throw if positions are invalid
              return false;
            }
          }

          return false;
        },
        mouseup() {
          dragWrapper?.classList.remove('is-cell-dragging');
          anchorCellPos = null;
          dragWrapper = null;
          return false;
        },
      },
    },
  });
};

const headerCleanupPluginKey = new PluginKey('headerCleanup');

const headerCleanupPlugin = () =>
  new Plugin({
    key: headerCleanupPluginKey,
    appendTransaction(transactions, _oldState, newState) {
      // Only run if there was a document change
      if (!transactions.some((tr) => tr.docChanged)) return null;

      // Get changed ranges to limit table scanning
      const changedRanges = getChangedRanges(transactions);
      if (changedRanges.length === 0) return null;

      // Collect unique tables within changed ranges
      const tables = new Map<number, { node: ProseMirrorNode; pos: number }>();
      for (const range of changedRanges) {
        const from = Math.max(0, range.from);
        const to = Math.min(newState.doc.content.size, range.to);
        if (from >= to) continue;

        newState.doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.name === 'table' && !tables.has(pos)) {
            tables.set(pos, { node, pos });
            return false; // Don't descend into table
          }
          return true;
        });
      }

      if (tables.size === 0) return null;

      let tr: Transaction | null = null;

      for (const { node, pos } of tables.values()) {
        const firstRow = node.child(0);
        if (!firstRow) continue;

        // Check if first row has headerStyled flag (or migrate from old gray bg)
        let hasHeaderStyle = false;
        for (let i = 0; i < firstRow.childCount; i++) {
          const cell = firstRow.child(i);
          // Check headerStyled flag, or migrate legacy docs with gray background
          if (
            cell.attrs.headerStyled ||
            (!cell.attrs.headerStyled &&
              cell.attrs.backgroundColor === HEADER_BG_COLOR)
          ) {
            hasHeaderStyle = true;
            break;
          }
        }

        // Sync headerStyled to new cells (no background) in styled header row
        if (hasHeaderStyle) {
          let cellOffset = pos + 2; // +1 for table, +1 for row
          for (let i = 0; i < firstRow.childCount; i++) {
            const cell = firstRow.child(i);
            // Only apply header styling to cells without any background (newly added)
            // Don't override cells that already have a custom background color
            const isNewCell =
              !cell.attrs.headerStyled && !cell.attrs.backgroundColor;
            if (isNewCell) {
              if (!tr) tr = newState.tr;
              tr.setNodeMarkup(cellOffset, undefined, {
                ...cell.attrs,
                headerStyled: true,
                backgroundColor: HEADER_BG_COLOR,
              });
            }
            cellOffset += cell.nodeSize;
          }
        }

        // Strip headerStyled from non-first rows (leave backgroundColor alone)
        for (let rowIndex = 1; rowIndex < node.childCount; rowIndex++) {
          const row = node.child(rowIndex);
          let cellOffset = pos + 1; // +1 for table open

          // Skip to this row
          for (let i = 0; i < rowIndex; i++) {
            cellOffset += node.child(i).nodeSize;
          }
          cellOffset += 1; // +1 for row open

          for (let cellIndex = 0; cellIndex < row.childCount; cellIndex++) {
            const cell = row.child(cellIndex);
            if (cell.attrs.headerStyled) {
              if (!tr) tr = newState.tr;
              tr.setNodeMarkup(cellOffset, undefined, {
                ...cell.attrs,
                headerStyled: false,
              });
            }
            cellOffset += cell.nodeSize;
          }
        }
      }

      return tr;
    },
  });

// Clipboard helpers (formatter, plugin, cell serializer) live in
// `table-clipboard.ts` so they can be unit/integration-tested without
// pulling React into the test bundle.

const TableWithPlugins = Table.extend({
  // Priority above tiptap-markdown's MarkdownClipboard (default 100) so our
  // clipboardTextSerializer wins someProp dispatch.
  priority: 200,
  // tiptap creates the `.tableWrapper` div only through the column-resizing
  // plugin, which it leaves out of read-only editors. Every table style is
  // scoped to that wrapper, so a table without one renders bare and overflows
  // its container. This is the view tiptap itself would attach.
  addNodeView() {
    return ({ node }: NodeViewRendererProps) =>
      new TableView(node, this.options.cellMinWidth);
  },
  addProseMirrorPlugins() {
    const parent = (this as unknown as { parent?: () => unknown[] }).parent;
    const plugins = [
      ...(parent?.() ?? []),
      headerCleanupPlugin(),
      tableClipboardTextPlugin(this.editor),
    ];
    // The controls below mount into `.tableWrapper` and dispatch edits, so they
    // belong to editable editors only. Editability is read once here, the same
    // gate tiptap uses for `columnResizing`: editors that flip between view and
    // edit remount, they do not call `setEditable` (which leaves plugins alone).
    if (!this.editor.isEditable) return plugins;
    return [
      ...plugins,
      activeCellPlugin(this.editor),
      cellDragSelectionPlugin(this.editor),
      tableControlsPlugin(this.editor),
    ];
  },
});

const cellAttributes = {
  backgroundColor: {
    default: null,
    parseHTML: (element: HTMLElement) => element.style.backgroundColor || null,
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.backgroundColor) return {};
      return { style: `background-color: ${attributes.backgroundColor}` };
    },
  },
  headerStyled: {
    default: false,
    parseHTML: (element: HTMLElement) =>
      element.getAttribute('data-header-styled') === 'true',
    renderHTML: (attributes: Record<string, unknown>) => {
      if (!attributes.headerStyled) return {};
      return { 'data-header-styled': 'true' };
    },
  },
};

const TableCellWithBg = TableCell.extend({
  addAttributes() {
    const parent = (
      this as unknown as { parent?: () => Record<string, unknown> }
    ).parent;
    return {
      ...parent?.(),
      ...cellAttributes,
    };
  },
});

const TableHeaderWithBg = TableHeader.extend({
  addAttributes() {
    const parent = (
      this as unknown as { parent?: () => Record<string, unknown> }
    ).parent;
    return {
      ...parent?.(),
      ...cellAttributes,
    };
  },
});

const HEADER_BG_COLOR = 'var(--gray-4)';

function setCellBackgroundColor(editor: Editor, color: string | null): boolean {
  const cellInfo = findCurrentCellInfo(editor);
  if (!cellInfo) return false;

  const { cellPos, cellNode } = cellInfo;
  const { tr } = editor.state;

  // When setting gray background on first-row cell, explicitly set headerStyled
  // to avoid implicit coupling in headerCleanupPlugin
  const inFirstRow = isInFirstRow(editor);
  const headerStyled =
    inFirstRow && color === HEADER_BG_COLOR
      ? true
      : inFirstRow && color !== HEADER_BG_COLOR
        ? false
        : cellNode.attrs.headerStyled;

  tr.setNodeMarkup(cellPos, undefined, {
    ...cellNode.attrs,
    backgroundColor: color,
    headerStyled,
  });
  editor.view.dispatch(tr);
  return true;
}

const CELL_COLORS = [
  { name: 'None', value: null },
  { name: 'Purple', value: 'var(--purple-4)' },
  { name: 'Red', value: 'var(--red-4)' },
  { name: 'Yellow', value: 'var(--yellow-4)' },
  { name: 'Blue', value: 'var(--blue-4)' },
  { name: 'Green', value: 'var(--green-4)' },
  { name: 'Orange', value: 'var(--orange-4)' },
  { name: 'Pink', value: 'var(--pink-4)' },
  { name: 'Gray', value: 'var(--gray-4)' },
] as const;

function ColorSwatch({ color }: { color: string | null }) {
  return (
    <div
      className="size-4 rounded border border-border-strongest"
      style={{ backgroundColor: color ?? 'transparent' }}
    />
  );
}

function isInFirstRow(editor: Editor): boolean {
  const { $from } = editor.state.selection;
  const tableInfo = findTableInfo($from);
  if (!tableInfo) return false;

  const rowInfo = findCurrentRowInfo(editor);
  if (!rowInfo) return false;

  // Check if the row position is the first row in the table
  const firstRowPos = tableInfo.pos + 1;
  return rowInfo.rowPos === firstRowPos;
}

function isInFirstColumn(editor: Editor): boolean {
  const rowInfo = findCurrentRowInfo(editor);
  if (!rowInfo) return false;

  const cellInfo = findCurrentCellInfo(editor);
  if (!cellInfo) return false;

  // First cell in row starts right after row opening
  const firstCellPos = rowInfo.rowPos + 1;
  return cellInfo.cellPos === firstCellPos;
}

function isHeaderRowStyled(editor: Editor): boolean {
  const { $from } = editor.state.selection;
  const tableInfo = findTableInfo($from);
  if (!tableInfo) return false;

  const firstRow = tableInfo.node.child(0);
  if (!firstRow) return false;

  // Check if any cell in the first row has headerStyled flag
  for (let i = 0; i < firstRow.childCount; i++) {
    const cell = firstRow.child(i);
    if (cell.attrs.headerStyled) return true;
  }
  return false;
}

function toggleHeaderRowBackground(editor: Editor): boolean {
  const { $from } = editor.state.selection;
  const tableInfo = findTableInfo($from);
  if (!tableInfo) return false;

  const firstRow = tableInfo.node.child(0);
  if (!firstRow) return false;

  const isStyled = isHeaderRowStyled(editor);
  const newHeaderStyled = !isStyled;

  const { tr } = editor.state;
  let cellPos = tableInfo.pos + 2; // +1 for table, +1 for row

  for (let i = 0; i < firstRow.childCount; i++) {
    const cell = firstRow.child(i);
    // When enabling header, set both flags
    // When disabling, clear headerStyled and clear gray background (but preserve custom colors)
    const newAttrs = newHeaderStyled
      ? { ...cell.attrs, headerStyled: true, backgroundColor: HEADER_BG_COLOR }
      : {
          ...cell.attrs,
          headerStyled: false,
          backgroundColor:
            cell.attrs.backgroundColor === HEADER_BG_COLOR
              ? null
              : cell.attrs.backgroundColor,
        };
    tr.setNodeMarkup(cellPos, undefined, newAttrs);
    cellPos += cell.nodeSize;
  }

  editor.view.dispatch(tr);
  return true;
}

export const tableExtensions = [
  TableWithPlugins.configure({ resizable: true }),
  TableRow.configure({ HTMLAttributes: { class: 'lb-editor-table-row' } }),
  TableHeaderWithBg.configure({
    HTMLAttributes: { class: 'lb-editor-table-cell' },
  }),
  TableCellWithBg.configure({
    HTMLAttributes: { class: 'lb-editor-table-cell' },
  }),
];
